import { type KeyboardEvent } from 'react';

import {
  type JourneyNodeId,
  type PositionedJourneyNode,
  journeyNodePercentage,
  JourneyNodeStatus,
} from './journeyFlowModel';
import {
  journeyPercentageLabel,
  journeyNodeColor,
  journeyCountLabel,
} from './journeyFlowPresentation';

export function JourneySankeyNode(props: {
  node: PositionedJourneyNode;
  height: number;
  hoverHeight: number;
  active: boolean;
  selected: boolean;
  onSelect: (id: JourneyNodeId) => void;
  onHover: (id: JourneyNodeId | null) => void;
}) {
  const percentage = journeyNodePercentage(props.node);
  const formattedCount = journeyCountLabel(props.node);
  const labelY = props.node.y + Math.max(2, props.height / 2) - 1;

  const handleKeyDown = (event: KeyboardEvent<SVGGElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    props.onSelect(props.node.id);
  };

  return (
    <g
      role="button"
      tabIndex={0}
      aria-pressed={props.selected}
      aria-label={`${props.node.label}, ${formattedCount}${percentage === null ? '' : `, ${journeyPercentageLabel(props.node, percentage)}`}`}
      opacity={props.active ? 1 : 0.18}
      className="cursor-pointer transition-opacity outline-none focus-visible:[&_rect[data-focus]]:stroke-[var(--ring)] focus-visible:[&_rect[data-focus]]:stroke-2"
      onClick={() => {
        props.onSelect(props.node.id);
      }}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => {
        props.onHover(props.node.id);
      }}
      onFocus={() => {
        props.onHover(props.node.id);
      }}
      onBlur={() => {
        props.onHover(null);
      }}
    >
      <rect
        pointerEvents="all"
        data-focus="true"
        x={props.node.x - 6}
        y={props.node.y - 8}
        width="176"
        height={props.hoverHeight}
        rx="4"
        fill="transparent"
        stroke="transparent"
      />
      <rect
        pointerEvents="none"
        x={props.node.x}
        y={props.node.y}
        width="7"
        height={props.height}
        rx="1"
        fill={journeyNodeColor(props.node)}
      />
      <text
        pointerEvents="none"
        x={props.node.x + 12}
        y={labelY}
        fill={
          props.node.status === JourneyNodeStatus.Unavailable
            ? 'var(--muted-foreground)'
            : 'var(--foreground)'
        }
        paintOrder="stroke"
        stroke="var(--card)"
        strokeWidth="4"
        strokeLinejoin="round"
        className="max-sm:text-[14px]"
        fontSize="10"
        fontWeight="600"
      >
        {props.node.label}
      </text>
      <text
        pointerEvents="none"
        x={props.node.x + 12}
        y={labelY + 12}
        fill="var(--muted-foreground)"
        className="max-sm:translate-y-1 max-sm:text-[12px]"
        fontSize="9"
        paintOrder="stroke"
        stroke="var(--card)"
        strokeWidth="4"
        strokeLinejoin="round"
      >
        {`${formattedCount}${percentage === null ? '' : ` · ${journeyPercentageLabel(props.node, percentage)}`}`}
      </text>
    </g>
  );
}
